import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, Loader2, MessageSquarePlus, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const SUGGESTIONS = ["What's due this week?", "What do I need to pack for tomorrow?", "Summarise today's brief in 3 bullets", "What lessons do I have tomorrow?"];
const newSession = () => `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const Bubble = ({ m, i }) => (
  <div data-testid={`ask-msg-${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-slate-900 text-black rounded-br-md" : "bg-surface border border-slate-200 text-slate-800 rounded-bl-md"}`}>
      {m.text || <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
    </div>
  </div>
);

export default function Ask() {
  const [session, setSession] = useState(() => localStorage.getItem("daybreak-ask-session") || newSession());
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => {
    let live = true;
    localStorage.setItem("daybreak-ask-session", session);
    api.get(`/ask/${session}`).then((r) => { if (live) setMsgs((cur) => (cur.length ? cur : Array.isArray(r.data) ? r.data : [])); }).catch(() => {});
    return () => { live = false; };
  }, [session]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs]);

  const send = async (q) => {
    const question = (q ?? text).trim();
    if (!question || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: question }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ask`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: session, message: question }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(typeof d?.detail === "string" ? d.detail : "Couldn't reach Daybreyk"); }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        setMsgs((m) => {
          const last = m[m.length - 1];
          if (last?.role !== "assistant") return m;
          return [...m.slice(0, -1), { ...last, text: last.text + chunk }];
        });
      }
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => m.slice(0, -2));
    }
    setBusy(false);
  };

  return (
    <main data-testid="ask-page" className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col min-h-[calc(100dvh-4rem)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-slate-900">Ask Daybreyk</h1>
          <p className="mt-2 text-slate-600">Questions about your brief, homework, timetable and kit. Powered by ChatGPT.</p>
        </div>
        <button data-testid="ask-new-chat" onClick={() => { setSession(newSession()); setMsgs([]); }} className="inline-flex items-center gap-2 shrink-0 rounded-xl border border-slate-200 bg-surface px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          <MessageSquarePlus className="w-4 h-4" /><span className="hidden sm:inline">New chat</span>
        </button>
      </div>
      <section data-testid="ask-messages" className="flex-1 mt-8 space-y-3">
        {msgs.length === 0 && (
          <div className="grid gap-2 sm:grid-cols-2">
            {SUGGESTIONS.map((s, i) => (
              <button key={s} data-testid={`ask-suggestion-${i}`} onClick={() => send(s)} className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-surface p-4 text-left text-sm font-medium text-slate-700 hover:border-teal-300 hover:bg-teal-50/40 transition-colors">
                <Sparkles className="w-4 h-4 text-teal-600 shrink-0" />{s}
              </button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        <div ref={end} />
      </section>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="sticky bottom-0 mt-6 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:pb-4 bg-gradient-to-t from-black via-black to-transparent pt-4">
        <div className="flex items-end gap-2 rounded-2xl border border-slate-200 bg-surface p-2 shadow-sm focus-within:border-teal-400">
          <textarea data-testid="ask-input" value={text} onChange={(e) => setText(e.target.value)} rows={1} maxLength={2000} placeholder="Ask anything about your day…"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="flex-1 resize-none bg-transparent px-2 py-2 text-base outline-none placeholder:text-slate-400" />
          <button data-testid="ask-send-button" type="submit" disabled={busy || !text.trim()} aria-label="Send" className="grid place-items-center w-10 h-10 rounded-xl bg-brand text-white hover:bg-brand-dark disabled:opacity-40 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </main>
  );
}
